import {
  ChangeDetectorRef, Component, OnInit, OnDestroy, Input, Output, EventEmitter,
  ViewEncapsulation, ViewChild, HostListener, ElementRef
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { CoreConfigService } from '@core/services/config.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreMenuItem } from '@core/types';

import { AppPermissionsService, AuthenticationService, AuthenticationTypeService, CompanyService, FavoriteService, RecentService } from '@common/services';
import { User, Favorite, FavoriteDTO, Company } from '@common/models';
import { ReportService } from '@bpm/services';
import { environment } from 'environments/environment';
import { InsightsService, MSGraphService } from '@core/services';
import { blobToBase64, fileVisualizeUrl, isImageIcon, isLucideIcon, lucideIconName } from '@core/utils';
import { BRAIN_COLORS } from '../../../colors.brain.const';
import { ChatDockService } from 'app/components/chat-ai/services/chat-dock.service';
import { HeadquartersService } from '@repositories/services';
import { HOME_NAV_URLS } from './home-header.constants';
import { UploadManagerService } from 'app/services/upload-manager.service';
import { LayoutSwitchService } from 'app/services/layout-switch.service';

@Component({
  selector: 'home-header',
  templateUrl: './home-header.component.html',
  styleUrls: ['./home-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class HomeHeaderComponent implements OnInit, OnDestroy {
  @Input() navbarPinned = true;
  @Output() pinToggle = new EventEmitter<void>();

  currentUser: User;
  currentSkin = 'default';
  prevSkin: string;
  profilePhoto: any;
  canSeeBIX: boolean;
  isDev = !environment.production;
  public environment: any = environment;

  companies: Company[] = [];
  selectedCompany: Company;

  selectedLanguage: string;
  languageOptions: Record<string, { title: string; flag: string }> = {
    en: { title: 'English', flag: 'us' },
    es: { title: 'Español', flag: 'es' }
  };

  searchTerm = '';

  // Nav fija (5 items extraídos del menú)
  navItems: CoreMenuItem[] = [];

  // Scroll-hide del pill nav cuando no está pinned
  navHidden = false;
  topBarFixed = false;

  // Alto real de la barra, para reservar su lugar cuando queda fija y que el
  // contenido no salte hacia arriba
  topBarSpacer = 0;

  // Scroll a partir del cual la barra queda fija, y por debajo del cual se suelta
  private readonly _TOP_BAR_FIX = 80;
  private readonly _TOP_BAR_RELEASE = 40;
  // Revela el header al hacer hover en el borde superior (modo auto-ocultar)
  hoverRevealed = false;
  private _lastScrollY = 0;

  // Mobile drawer (responsive)
  mobileMenuOpen = false;
  menuItems: CoreMenuItem[] = [];
  expandedKeys: Record<string, boolean> = {};

  headerBgStyle: Record<string, string> = { 'background-color': '#4B4B4B' };

  // Fondos ya descargados: al reentrar o cambiar de tema se aplican sin pasar
  // otra vez por el color por defecto
  private _loadedBgUrls = new Set<string>();
  // Fondo que se está descargando, para descartar cargas viejas si se navega
  private _pendingBgUrl: string | null = null;

  // Api key para visualizar los backgrounds guardados en belivfiles (?code=)
  private _apiKey = '';

  /** Expone el helper al template: el icono del menú puede ser clase o imagen */
  readonly isImageIcon = isImageIcon;

  /** Los iconos elegidos del catálogo se guardan como 'lucide:<nombre>' */
  readonly isLucideIcon = isLucideIcon;
  readonly lucideIconName = lucideIconName;

  favorites: Favorite[] = [];
  currentFavorite: Favorite | null = null;
  isCurrentFavorite = false;
  favoriteLoading = false;

  bannerTitleKey = 'PAGES.HOME.HOME_BRAIN';
  showWelcomeTo = true;

  dashboardsCount: number | null = null;

  /** Momento actual del navegador; el reloj del banner lo refresca cada segundo */

  headquartersCount: number | null = null;

  // ── Frase aleatoria de bienvenida (solo en el inicio) ──────────────
  /** Está el usuario en la pantalla de inicio */
  isHome = false;
  /** Key de la frase que se muestra: COMMON.RANDOM_TEXT_INIT.TEXTn */
  welcomePhraseKey = '';
  /** Cantidad de frases COMMON.RANDOM_TEXT_INIT.TEXTn disponibles */
  private readonly _WELCOME_PHRASES = 10;

  /** Nombre con el que se saluda al usuario ({{user}} de la frase) */
  get userFirstName(): string {
    return this.currentUser?.person?.name || this.currentUser?.username || '';
  }

  private readonly _BANNER_MAP: Record<string, { key: string; welcome: boolean }> = {
    'pages/home':        { key: 'PAGES.HOME.HOME_BRAIN',  welcome: true  },
    'news':              { key: 'PAGES.HOME.NEWS',         welcome: false },
    'dashboards':        { key: 'PAGES.HOME.DASHBOARDS',  welcome: false },
    'apps/repositories': { key: 'PAGES.HOME.BELIVPEDIA',   welcome: false },
    'apps/collections':  { key: 'PAGES.HOME.APPS',         welcome: false },
  };

  /** Accesos fijos de la barra; el explorador de Apps usa la misma lista para excluirlos */
  private readonly _NAV_URLS = HOME_NAV_URLS;

  private readonly _NAV_LABEL_OVERRIDES: Record<string, string> = {
    'apps/repositories': 'Belivpedia'
  };

  @ViewChild('popoverBix') popoverBix: NgbPopover;
  @ViewChild('topBar') topBar: ElementRef<HTMLElement>;

  private _unsubscribeAll = new Subject<any>();

  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _permissionsService: AppPermissionsService,
    private _companyService: CompanyService,
    private _coreConfigService: CoreConfigService,
    private _coreMenuService: CoreMenuService,
    public _translateService: TranslateService,
    private _msGraphService: MSGraphService,
    private _favoriteService: FavoriteService,
    private _reportService: ReportService,
    private _authenticationTypeService: AuthenticationTypeService,
    private _recentService: RecentService,
    private _cdr: ChangeDetectorRef,
    private _chatDockService: ChatDockService,
    private _headquartersService: HeadquartersService,
    private _insightsService: InsightsService,
    public uploadManagerService: UploadManagerService,
    public layoutSwitch: LayoutSwitchService
  ) {
    this.canSeeBIX = this._permissionsService.hasPermissionByUrl('pages/chat', 'READ');
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.navbarPinned) {
      // Se decide con la posición del scroll, NO midiendo el header: al fijar la
      // barra ésta sale del flujo y el header se encoge, así que su rect.bottom
      // quedaba siempre por debajo del umbral y la barra no volvía a soltarse.
      // Los dos umbrales (histéresis) evitan que titile en el límite.
      const current = window.scrollY;
      const fixed = current > (this.topBarFixed ? this._TOP_BAR_RELEASE : this._TOP_BAR_FIX);

      // El alto se mide con la barra todavía en el flujo, para reservar su lugar
      if (fixed && !this.topBarFixed) this._measureTopBar();

      this.topBarFixed = fixed;
      this.navHidden = false;
    } else {
      this.topBarFixed = false;
      const current = window.scrollY;
      this.navHidden = current > this._lastScrollY && current > 60;
      this._lastScrollY = current;
      // Al volver arriba del todo ya no tiene sentido mantener el hover-reveal
      if (current <= 60) this.hoverRevealed = false;
    }
  }

  /**
   * En modo auto-ocultar (candado abierto), revela el header cuando el mouse
   * se acerca al borde superior de la pantalla y lo vuelve a ocultar al alejarse.
   */
  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (this.navbarPinned) return;

    // Solo tiene sentido revelar si el usuario ya scrolleó hacia abajo
    const scrolled = window.scrollY > 60;

    // Histéresis: se abre cerca del borde (≤ 60px) y se mantiene mientras
    // el mouse esté sobre el header revelado (≤ 110px)
    const zone = this.hoverRevealed ? 110 : 60;
    const reveal = scrolled && e.clientY <= zone;

    if (reveal !== this.hoverRevealed) {
      this.hoverRevealed = reveal;
      this._cdr.detectChanges();
    }
  }

  /** Guarda el alto de la barra mientras todavía ocupa lugar en el flujo */
  private _measureTopBar(): void {
    const height = this.topBar?.nativeElement?.offsetHeight || 0;
    if (height > 0) this.topBarSpacer = height;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  toggleExpand(key: string): void {
    this.expandedKeys[key] = !this.expandedKeys[key];
  }

  onLeafClick(): void {
    this.closeMobileMenu();
  }

  trackMenu(i: number, item: CoreMenuItem): string {
    return item.url || item.title + i;
  }

  setLanguage(language: string): void {
    this.selectedLanguage = language;
    this._translateService.use(language);
    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  toggleDarkSkin(): void {
    this.prevSkin = localStorage.getItem('prevSkin');
    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin || 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  /**
   * Indica si el usuario tiene acceso a más de una empresa
   */
  get showCompanySelector(): boolean {
    return this.companies.length > 1;
  }

  /**
   * Obtiene las empresas a las que tiene acceso el usuario
   */
  private _loadUserCompanies(): void {
    console.log('[COMPANY-SELECTOR] 1. Solicitando GET /api/companies/user ...');

    this._companyService.getUserCompanies()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: resp => {
          console.log('[COMPANY-SELECTOR] 2. OK. Empresas recibidas:', resp);

          this.companies = resp || [];

          Promise.resolve().then(() => {
            this._syncSelectedCompany();
            console.log('[COMPANY-SELECTOR] 4. showCompanySelector:', this.showCompanySelector,
              '(requiere > 1 empresa, hay', this.companies.length + ')');
            this._cdr.detectChanges();
          });
        },
        error: err => {
          // Sin este callback el error sería silencioso y el selector simplemente no aparecería
          console.error('[COMPANY-SELECTOR] 2. FALLÓ GET /api/companies/user →', err.status, err.statusText, err);

          if (err.status === 400 || err.status === 404) {
            console.error('[COMPANY-SELECTOR] El backend corriendo no tiene el endpoint nuevo. ' +
              'Un 400 significa que la petición cayó en /api/companies/{id} intentando convertir "user" a número. ' +
              'Recompilá y redesplegá beliv-apirest-common.');
          }
        }
      });
  }

  /**
   * Sincroniza la empresa mostrada en el selector con la empresa activa
   */
  private _syncSelectedCompany(): void {
    const currentCompanyId = this._authenticationService.currentCompanyIdValue;

    console.log('[COMPANY-SELECTOR] 3. Sincronizando. Empresa activa (cid):', currentCompanyId,
      '| empresas cargadas:', this.companies.length);

    // Espera a que la empresa activa y las empresas del usuario estén disponibles
    if (!currentCompanyId || !this.companies.length) {
      console.warn('[COMPANY-SELECTOR] 3.1 Aún no hay empresa activa o empresas; se reintenta al emitir onCompanyIdChanged.');
      return;
    }

    this.selectedCompany = this.companies.find(company => company.id === currentCompanyId);

    // Si la empresa activa ya no pertenece al usuario, se posiciona en la primera disponible
    if (!this.selectedCompany) {
      this._authenticationService.switchCompany(this.companies[0].id);
    }
  }

  /**
   * Cambia la empresa activa del usuario
   *
   * @param company
   */
  changeCompany(company: Company): void {
    // Valida que sea una empresa distinta a la actual
    if (!company || company.id === this._authenticationService.currentCompanyIdValue) return;

    console.log('[COMPANY-SELECTOR] Cambiando empresa →', company.id, company.name,
      '| las siguientes peticiones deben viajar con el header cid =', company.id);

    // Cambia la empresa en memoria y recarga los permisos, menús y estados
    this._authenticationService.switchCompany(company.id);

    // Cierra el drawer, ya que en móvil el selector se encuentra dentro del mismo
    this.closeMobileMenu();

    // Navega al inicio ya que la ruta actual puede no estar permitida en la nueva empresa
    this._router.navigate(['/']);
  }

  onPinToggle(): void {
    this.pinToggle.emit();
  }

  onFavorite(): void {
    if (this.favoriteLoading || !this.currentUser) return;

    const cleanUrl = this._router.url.replace(/^\//, '').split('?')[0];

    // Caso especial: cuando estamos viendo un dashboard concreto
    // (/dashboards/view/{id}) cada reporte debe ser su propio favorito
    const dashboardViewMatch = cleanUrl.match(/^dashboards\/view\/(\d+)$/);
    if (dashboardViewMatch) {
      const reportId = dashboardViewMatch[1];
      this.favoriteLoading = true;
      this._reportService.getById(Number(reportId))
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: (report) => {
            const dto: FavoriteDTO = {
              userId: this._getFavoriteUserId(),
              resourceId: String(report?.id ?? reportId),
              resourceType: 'DASHBOARDS',
              resourceName: report?.title || `Dashboard ${reportId}`,
              resourceUrl: '/' + cleanUrl,
              thumbnailColor: BRAIN_COLORS.light.colorFavorites,
              icon: 'fa fa-wrench',
              image: report?.image || undefined
            };
            this._sendToggle(dto);
          },
          error: () => {
            // Si falla obtener el reporte, al menos guarda con el id de la URL
            const dto: FavoriteDTO = {
              userId: this._getFavoriteUserId(),
              resourceId: reportId,
              resourceType: 'DASHBOARDS',
              resourceName: `Dashboard ${reportId}`,
              resourceUrl: '/' + cleanUrl,
              thumbnailColor: BRAIN_COLORS.light.colorFavorites,
              icon: 'fa fa-wrench'
            };
            this._sendToggle(dto);
          }
        });
      return;
    }

    // Caso general: derivar del menú
    const allItems = [...this.navItems, ...this.menuItems];
    const matched = this._findMenuItemByUrl(allItems, cleanUrl);

    const dto: FavoriteDTO = {
      userId: this._getFavoriteUserId(),
      resourceId: matched?.id || cleanUrl,
      resourceType: this._getResourceType(cleanUrl),
      resourceName: matched?.title || cleanUrl,
      resourceUrl: '/' + cleanUrl,
      thumbnailColor: BRAIN_COLORS.light.colorFavorites,
      icon: matched?.icon,
      image: matched?.backgroundUrl
    };
    this._sendToggle(dto);
  }

  private _sendToggle(dto: FavoriteDTO): void {
    this.favoriteLoading = true;
    this._favoriteService.toggle(dto)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: resp => {
          this.favoriteLoading = false;
          if (resp.isFavorite && resp.favorite) {
            this.favorites = [...this.favorites, resp.favorite];
          } else {
            this.favorites = this.favorites.filter(
              f => !(f.resourceId === dto.resourceId && f.resourceType === dto.resourceType)
            );
          }
          this._refreshIsCurrentFavorite(this._router.url);
        },
        error: () => { this.favoriteLoading = false; }
      });
  }

  private _getFavoriteUserId(): string {
    return this.currentUser?.username || this.currentUser?.email || '';
  }

  private _getResourceType(cleanUrl: string): string {
    const seg = cleanUrl.split('/')[0] || 'PAGE';
    return seg.toUpperCase();
  }

  private _loadDashboardsCount(): void {
    this._reportService.getCountByType('Dashboard')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: count => {
          this.dashboardsCount = count;
          this._cdr.detectChanges();
        }
      });
  }

  private _loadHeadquartersCount(): void {
    this._headquartersService.count()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: count => {
          this.headquartersCount = count;
          this._cdr.detectChanges();
        }
      });
  }

  private _loadFavorites(): void {
    const userId = this._getFavoriteUserId();
    if (!userId) return;
    this._favoriteService.findByUserId(userId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: list => {
          this.favorites = list || [];
          this._refreshIsCurrentFavorite(this._router.url);
        }
      });
  }

  private _refreshIsCurrentFavorite(routerUrl: string): void {
    const cleanUrl = routerUrl.replace(/^\//, '').split('?')[0];
    const target = '/' + cleanUrl;
    this.currentFavorite = this.favorites.find(f => f.resourceUrl === target) || null;
    this.isCurrentFavorite = !!this.currentFavorite;
    this._cdr.detectChanges();
  }

  onSearchSubmit(e: Event): void {
    e.preventDefault();
    const q = (this.searchTerm || '').trim();
    if (!q) return;
    // Cierra el drawer móvil si estaba abierto y navega a la página de resultados
    this.mobileMenuOpen = false;
    this._router.navigate(['/search'], { queryParams: { q } });
  }

  private _loadMenuItems(): void {
    const current = this._coreMenuService.getCurrentMenu() as CoreMenuItem[];
    if (!current) return;
    this.menuItems = current.filter(it => !it.hidden);
    this.navItems = this._NAV_URLS
      .map(url => current.find(it => it.url === url))
      .filter(Boolean) as CoreMenuItem[];
    this._updateHeaderBg(this._router.url);
    // Al entrar directo por url el menú llega después del NavigationEnd, así que
    // se reintenta registrar la visita cuando ya se puede resolver
    this._trackRecent(this._router.url);
    this._cdr.detectChanges();
  }

  /**
   * Registra la pantalla actual en los recientes (localStorage, últimos 9).
   * Solo se guardan pantallas que existen en el menú del usuario; el inicio no.
   * @param routerUrl Url actual del router
   */
  private _trackRecent(routerUrl: string): void {
    const cleanUrl = (routerUrl || '').replace(/^\//, '').split('?')[0];
    if (!cleanUrl || cleanUrl.startsWith('pages/home')) return;

    const userId = this._getFavoriteUserId();
    if (!userId) return;

    const matched = this._findBestMenuItem([...this.navItems, ...this.menuItems], cleanUrl);
    if (!matched) return;

    this._recentService.add(userId, {
      id: matched.id,
      name: this.getNavLabel(matched),
      url: '/' + cleanUrl,
      icon: matched.icon,
      image: matched.backgroundUrl
    });
  }

  /**
   * Busca el item de menú más específico para una url. A diferencia de
   * _findMenuItemByUrl (que devuelve el primer match y puede quedarse con el
   * padre), acá gana la url más larga: para 'apps/menus' devuelve el hijo y no
   * el padre 'apps'.
   */
  private _findBestMenuItem(items: CoreMenuItem[], url: string): CoreMenuItem | null {
    const matches: CoreMenuItem[] = [];
    const walk = (list: CoreMenuItem[]) => {
      (list || []).forEach(it => {
        if (it.url && (url === it.url || url.startsWith(it.url + '/'))) matches.push(it);
        if (it.children?.length) walk(it.children);
      });
    };
    walk(items);
    return matches.sort((a, b) => (b.url?.length || 0) - (a.url?.length || 0))[0] || null;
  }

  private _findMenuItemByUrl(items: CoreMenuItem[], url: string): CoreMenuItem | null {
    for (const item of items) {
      if (item.url && url.includes(item.url)) return item;
      if (item.children?.length) {
        const found = this._findMenuItemByUrl(item.children, url);
        if (found) return found;
      }
    }
    return null;
  }

  /** ¿Se está viendo un dashboard? (/dashboards/view/:id) */
  private _isDashboardViewer(cleanUrl: string): boolean {
    return /^dashboards\/view(\/|$)/i.test(cleanUrl);
  }

  /** Fondo por defecto: color plano del tema activo */
  private _defaultHeaderBg(): Record<string, string> {
    const palette = this.currentSkin === 'dark' ? BRAIN_COLORS.dark : BRAIN_COLORS.light;
    return { 'background': palette.headerFallbackBg };
  }

  /** Fondo con imagen */
  private _imageHeaderBg(bgUrl: string): Record<string, string> {
    return {
      'background-image': `url('${bgUrl}')`,
      'background-size': 'cover',
      'background-position': 'center',
      'background-repeat': 'no-repeat'
    };
  }

  /**
   * Aplica el fondo del header. La imagen se precarga y hasta que termina de
   * bajar se deja el color por defecto, así no se ve el hueco mientras carga.
   * Si falla la descarga se queda el color.
   * @param bgUrl Url de la imagen, o vacío para el fondo por defecto
   */
  private _applyHeaderBg(bgUrl?: string): void {
    if (!bgUrl) {
      this._pendingBgUrl = null;
      this.headerBgStyle = this._defaultHeaderBg();
      return;
    }

    // Ya descargada antes (otra visita, cambio de tema): se aplica directo
    if (this._loadedBgUrls.has(bgUrl)) {
      this._pendingBgUrl = null;
      this.headerBgStyle = this._imageHeaderBg(bgUrl);
      return;
    }

    this._pendingBgUrl = bgUrl;
    this.headerBgStyle = this._defaultHeaderBg();

    const img = new Image();
    img.onload = () => {
      this._loadedBgUrls.add(bgUrl);
      // Si mientras bajaba se cambió de pantalla, no se pisa el fondo nuevo
      if (this._pendingBgUrl !== bgUrl) return;
      this._pendingBgUrl = null;
      this.headerBgStyle = this._imageHeaderBg(bgUrl);
      this._cdr.detectChanges();
    };
    img.onerror = () => {
      if (this._pendingBgUrl === bgUrl) this._pendingBgUrl = null;
    };
    img.src = bgUrl;
  }

  private _updateHeaderBg(routerUrl: string): void {
    const cleanUrl = routerUrl.replace(/^\//, '').split('?')[0];
    const allItems = [...this.navItems, ...this.menuItems];
    const matched = this._findMenuItemByUrl(allItems, cleanUrl);

    // Viendo un dashboard la cinta va sin imagen; en el resto backgroundUrl
    // puede ser el nombre del archivo en belivfiles o una URL vieja
    const bgUrl = this._isDashboardViewer(cleanUrl)
      ? undefined
      : fileVisualizeUrl(matched?.backgroundUrl, this._apiKey);

    this._applyHeaderBg(bgUrl);

    // La frase de bienvenida solo va en el inicio y cambia cada vez que se vuelve
    const isHome = cleanUrl === '' || cleanUrl.startsWith('pages/home');
    if (isHome && !this.isHome) this._pickWelcomePhrase();
    this.isHome = isHome;

    const bannerEntry = Object.entries(this._BANNER_MAP).find(([url]) => cleanUrl.startsWith(url));
    if (bannerEntry) {
      this.bannerTitleKey = bannerEntry[1].key;
      this.showWelcomeTo = bannerEntry[1].welcome;
    } else {
      this.bannerTitleKey = 'PAGES.HOME.HOME_BRAIN';
      this.showWelcomeTo = true;
    }

    this._cdr.detectChanges();
  }

  /**
   * Título del item en el idioma activo. Los títulos vienen de mysql en el
   * objeto menú: `title` en español y `translate` (titleEnglish) en inglés.
   */
  getNavLabel(item: CoreMenuItem): string {
    const override = this._NAV_LABEL_OVERRIDES[item.url];
    if (override) return override;

    if (this.selectedLanguage === 'en') return item.translate || item.title;
    return item.title;
  }

  /**
   * Elige al azar una de las frases de bienvenida
   * (COMMON.RANDOM_TEXT_INIT.TEXT1 ... TEXT{_WELCOME_PHRASES})
   */
  private _pickWelcomePhrase(): void {
    const n = Math.floor(Math.random() * this._WELCOME_PHRASES) + 1;
    this.welcomePhraseKey = `COMMON.RANDOM_TEXT_INIT.TEXT${n}`;
  }

  /** Abre la comunidad de IA incrustada; la navegación la hace el routerLink */
  openAiCommunity(): void {
    this.log('Abrir comunidad de IA', { site: 'ai-community' });
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.Home, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.Home, message, data, user);
  }

  logout(): void {
    this._coreMenuService.unregister('main');
    this._authenticationService.logout();
    this._router.navigate(['/pages/authentication/login']);
  }

  ngOnInit(): void {

    this._authenticationService.onUserValueChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          this.currentUser = resp;
          if (localStorage.getItem('auth-type') === 'saml') {
            this._msGraphService.register();
          }
          this._loadFavorites();
          this._loadUserCompanies();

          this._loadDashboardsCount();
          this._loadHeadquartersCount();

          // Key para armar la URL del background cuando es un archivo de belivfiles
          this._authenticationTypeService.getApiKey()
            .then(key => {
              this._apiKey = (key as string) || '';
              this._updateHeaderBg(this._router.url);
            })
            .catch(() => { this._apiKey = ''; });

        }
      });

    // Empresa activa
    this._authenticationService.onCompanyIdChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          Promise.resolve().then(() => {
            this._syncSelectedCompany();
            this._loadDashboardsCount();
            this._loadHeadquartersCount();
            this._cdr.detectChanges();
          });
        }
      });

    this._msGraphService.onAccessTokenChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(resp => {
        if (resp) {
          this._msGraphService.getPhoto()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(photo => {
              blobToBase64(photo).then(value => {
                Promise.resolve().then(() => {
                  this.profilePhoto = value;
                  this._cdr.detectChanges();
                });
              });
            });
        }
      });

    this._permissionsService.onPermissionsChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(res => {
        if (res) {
          Promise.resolve().then(() => {
            this.canSeeBIX = this._permissionsService.hasPermissionByUrl('pages/chat', 'READ');
            this._cdr.detectChanges();
          });
        }
      });

    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
        this._updateHeaderBg(this._router.url);
      });

    this.selectedLanguage = this._translateService.currentLang || 'es';
    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(event => {
        this.selectedLanguage = event.lang;
        // Actualizando idioma de menús
        this._cdr.detectChanges();
      });

    // Suscripción al menú de la app (endpoint) para nav fija y drawer mobile
    this._coreMenuService.onMenuChanged
      .pipe(filter(v => v !== null), takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this._loadMenuItems();
      });

    // Actualiza background al cambiar de ruta
    this._router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._unsubscribeAll))
      .subscribe((e: NavigationEnd) => {
        this._updateHeaderBg(e.urlAfterRedirects);
        this._refreshIsCurrentFavorite(e.urlAfterRedirects);
        this._trackRecent(e.urlAfterRedirects);
      });
  }

  ngAfterViewInit(): void {
    if (this.popoverBix) {
      setTimeout(() => this.popoverBix.open(), 1000);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Abre o cierra el panel acoplado de Bryant.
   *
   * Antes este botón navegaba a /pages/chat; ahora el chat se despliega al
   * costado y el contenido de la app se encoge para dejarle espacio.
   */
  toggleBryant(): void {
    this._chatDockService.toggle();
  }

  /** Indica si el panel de Bryant está abierto, para resaltar el botón */
  get bryantOpen(): boolean {
    return this._chatDockService.isOpen;
  }
}
